/**
 * Pinterest Advertising OAuth Utilities (Story 19.4, Task 2)
 *
 * Handles OAuth 2.0 + PKCE URL building, code exchange, refresh, and error parsing
 * against the Pinterest API v5 endpoints
 * (`https://www.pinterest.com/oauth/` for authorize,
 *  `https://api.pinterest.com/v5/oauth/token` for token exchange + refresh).
 *
 * Key characteristics that distinguish Pinterest from MS Ads / Klaviyo:
 *  - HTTP Basic Auth on the token endpoint (Pinterest's documented contract is
 *    `Authorization: Basic ${base64(client_id:client_secret)}` — NOT form-body
 *    credentials. We use Workers-compatible `btoa()` because Cloudflare Workers
 *    don't ship Node's `Buffer`.
 *  - Continuous refresh tokens (60-day lifetime, refreshable indefinitely;
 *    rotated on every successful refresh per Pinterest's June 2025 newsletter).
 *  - Token prefixes: `pina` (user access), `pinr` (refresh), `pinc` (client_credentials).
 *  - Scopes are space-joined per OAuth 2.0 RFC 6749 (Pinterest accepts space or
 *    comma; we pick space for spec compliance).
 *  - PKCE (code_challenge_method=S256) included for defense-in-depth and to
 *    keep our OAuth flows uniform with MS Ads / Klaviyo. Pinterest tolerates
 *    PKCE on confidential clients without rejecting it.
 *
 * @see https://developers.pinterest.com/docs/api/v5/oauth-token/
 * @see https://developers.pinterest.com/docs/getting-started/set-up-authentication-and-authorization/
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";

// ============================================================================
// Constants
// ============================================================================

const PINTEREST_AUTHORIZE_URL = "https://www.pinterest.com/oauth/";
const PINTEREST_TOKEN_URL = "https://api.pinterest.com/v5/oauth/token";
const TOKEN_EXCHANGE_TIMEOUT_MS = 25_000;

/**
 * Scopes requested for Pinterest Advertising integration.
 *
 * `ads:read` grants visibility into ads/ad groups/campaigns; `user_accounts:read`
 * grants access to user_account + ad_accounts enumeration. Both are read-only —
 * we never write campaigns from ProvenLeap.
 */
export const PINTEREST_ADS_SCOPES = [
  "ads:read",
  "user_accounts:read",
] as const;

/**
 * Pinterest continuous-refresh refresh tokens last 60 days from the most
 * recent successful refresh. Calls to `refreshPinterestAdsToken` rotate the
 * refresh token and reset this lifetime — see ADR-020. Apps registered before
 * 2025-09-25 use legacy 365-day refresh tokens; apps registered after that
 * date (including ProvenLeap) auto-use continuous refresh.
 */
export const PINTEREST_ADS_REFRESH_TOKEN_LIFETIME_MS =
  60 * 24 * 60 * 60 * 1000;

// ============================================================================
// Types
// ============================================================================

export interface PinterestAdsAuthUrlOptions {
  clientId: string;
  redirectUri: string;
  /** KV state key to embed in state param */
  stateKey: string;
  /** PKCE code_challenge (Base64URL of SHA-256 of code_verifier) */
  codeChallenge: string;
}

export interface PinterestAdsTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
  /**
   * Continuous refresh tokens (apps registered after 2025-09-25) include the
   * remaining lifetime in seconds so callers know exactly when to refresh.
   * Legacy apps may omit this — callers must fall back to
   * `PINTEREST_ADS_REFRESH_TOKEN_LIFETIME_MS` when absent.
   */
  refresh_token_expires_in?: number;
  /**
   * Absolute Unix timestamp (seconds) when the refresh token expires. Pinterest
   * may return this alongside `refresh_token_expires_in`; we prefer the relative
   * `_in` form for arithmetic consistency but fall through to this if absent.
   */
  refresh_token_expires_at?: number;
}

export interface PinterestAdsCallbackError {
  code: string;
  userMessage: string;
  userAction: "retry" | "none";
}

// ============================================================================
// Build Authorization URL
// ============================================================================

/**
 * Builds the Pinterest API v5 OAuth 2.0 + PKCE authorization URL.
 *
 * Mirrors `buildMicrosoftAdsAuthUrl()` with Pinterest specifics:
 *   - Authorize URL is `https://www.pinterest.com/oauth/` (NOT `api.pinterest.com`)
 *   - `scope` is space-joined per OAuth 2.0 spec
 *   - No `prompt` param (Pinterest doesn't support one — multi-account selection
 *     happens via the in-flow account picker)
 */
export function buildPinterestAdsAuthUrl(
  options: PinterestAdsAuthUrlOptions
): string {
  const { clientId, redirectUri, stateKey, codeChallenge } = options;

  if (!clientId) {
    throw new Error("PINTEREST_ADS_CLIENT_ID is required");
  }
  if (!redirectUri) {
    throw new Error("PINTEREST_ADS_OAUTH_REDIRECT_URI is required");
  }
  if (!stateKey) {
    throw new Error("stateKey is required for CSRF protection");
  }
  if (!codeChallenge) {
    throw new Error("codeChallenge is required for PKCE");
  }

  const url = new URL(PINTEREST_AUTHORIZE_URL);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("state", stateKey);
  url.searchParams.set("scope", PINTEREST_ADS_SCOPES.join(" "));
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("code_challenge", codeChallenge);

  return url.toString();
}

// ============================================================================
// Internal: Workers-compatible HTTP Basic Auth header
// ============================================================================

/**
 * Builds the `Authorization: Basic ...` header value Pinterest's token endpoint
 * requires.
 *
 * MUST use `btoa()` (Web standard, available in Workers) NOT
 * `Buffer.from(...).toString("base64")` — the Node `Buffer` global is not
 * present in Cloudflare Workers and would throw `ReferenceError: Buffer is not
 * defined` on every token call. Confirmed via Pre-mortem F1 review.
 */
function buildBasicAuthHeader(clientId: string, clientSecret: string): string {
  return `Basic ${btoa(`${clientId}:${clientSecret}`)}`;
}

// ============================================================================
// Typed token-endpoint error (so adapter callers don't regex-match messages)
// ============================================================================

/**
 * Thrown by `exchangePinterestAdsCode` / `refreshPinterestAdsToken` when
 * Pinterest's token endpoint returns a non-2xx response. Carries the parsed
 * OAuth `error` code field (when present) so the adapter can route
 * `invalid_grant` → `token_expired` without string-matching the (sanitized)
 * message — addresses Code Review #1 finding 10.
 *
 * Constructor takes an options object so future fields (httpStatus, cause,
 * correlationId) can be added without breaking existing call sites — addresses
 * Code Review #2 finding 2.
 *
 * @example
 *   throw new PinterestAdsTokenError(
 *     "Pinterest Ads token refresh failed: invalid_grant: ...",
 *     { oauthError: "invalid_grant" }
 *   );
 */
export interface PinterestAdsTokenErrorOptions {
  /**
   * The verbatim value of the `error` field from Pinterest's JSON envelope
   * (e.g., `"invalid_grant"`, `"invalid_request"`); undefined when the
   * response body wasn't a parseable OAuth-shaped JSON envelope.
   */
  oauthError?: string;
}

export class PinterestAdsTokenError extends Error {
  readonly oauthError: string | undefined;
  constructor(message: string, options: PinterestAdsTokenErrorOptions = {}) {
    super(message);
    this.name = "PinterestAdsTokenError";
    this.oauthError = options.oauthError;
  }
}

/**
 * Pulls the OAuth `error` code out of a sanitized provider body. Returns
 * undefined when the body isn't JSON, doesn't carry an `error` field, or
 * carries it in an unexpected shape (e.g., nested object). Defensive shape
 * checks address Code Review #2 finding 1 / bonus.
 *
 * @internal exported for direct unit testing only
 */
export function extractOAuthErrorCode(providerBody: string | undefined): string | undefined {
  if (!providerBody) return undefined;
  try {
    const parsed: unknown = JSON.parse(providerBody);
    if (parsed === null || typeof parsed !== "object") return undefined;
    const err = (parsed as Record<string, unknown>).error;
    // Standard OAuth shape: `error` is a top-level string.
    if (typeof err === "string" && err.length > 0) {
      return err;
    }
    // Defensive: some providers nest the error code under `error.code` or
    // `error.error`. We don't currently see this from Pinterest, but
    // tolerating it costs nothing and avoids a silent regex-fallback if
    // Pinterest ever restructures their response.
    if (err !== null && typeof err === "object") {
      const nestedCode = (err as Record<string, unknown>).code;
      if (typeof nestedCode === "string" && nestedCode.length > 0) {
        return nestedCode;
      }
      const nestedError = (err as Record<string, unknown>).error;
      if (typeof nestedError === "string" && nestedError.length > 0) {
        return nestedError;
      }
    }
  } catch {
    // Not JSON — return undefined so callers fall back to the message body.
  }
  return undefined;
}

// ============================================================================
// Token Exchange
// ============================================================================

/**
 * Exchanges an authorization code for access + refresh tokens.
 *
 * CRITICAL: Pinterest requires `application/x-www-form-urlencoded` for the
 * token endpoint AND HTTP Basic Auth for client credentials (NOT form-body
 * `client_id`/`client_secret`). The `code_verifier` is sent so Pinterest can
 * validate the PKCE pair.
 */
export async function exchangePinterestAdsCode(
  code: string,
  codeVerifier: string,
  clientId: string,
  clientSecret: string,
  redirectUri: string
): Promise<PinterestAdsTokenResponse> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  });

  const result = await safeFetch(
    PINTEREST_TOKEN_URL,
    {
      method: "POST",
      headers: {
        Authorization: buildBasicAuthHeader(clientId, clientSecret),
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
    },
    TOKEN_EXCHANGE_TIMEOUT_MS
  );

  if (!result.success) {
    // safeFetch already sanitizes provider error bodies via providerMessage —
    // include it in the thrown error so refresh-callers can detect cases like
    // `invalid_grant` (refresh-token revoked) and surface a re-connect prompt.
    const detail = result.error.providerMessage
      ? extractOAuthErrorDescription(result.error.providerMessage)
      : "";
    const oauthError = extractOAuthErrorCode(result.error.providerMessage);
    throw new PinterestAdsTokenError(
      sanitizeErrorMessage(
        `Pinterest Ads token exchange failed: ${detail || result.error.message}`
      ),
      { oauthError }
    );
  }

  const response = result.data;
  // safeFetch only returns success=true when response.ok, so we can read JSON directly.
  const data = (await response.json()) as PinterestAdsTokenResponse;

  validateTokenResponse(data, /* requireRefreshToken */ true);

  return data;
}

// ============================================================================
// Token Refresh
// ============================================================================

/**
 * Refreshes a Pinterest Ads access token via the refresh_token grant.
 *
 * Pinterest's continuous refresh tokens explicitly rotate on every successful
 * refresh — callers MUST persist the rotated `refresh_token` AND the updated
 * `refresh_token_expires_at` if returned. Otherwise the next refresh will fail
 * with `invalid_grant`. See ADR-020.
 *
 * The `scope` body param is not strictly required, but sending it makes the
 * new token's scope explicit and matches the Microsoft Ads pattern.
 */
export async function refreshPinterestAdsToken(
  refreshToken: string,
  clientId: string,
  clientSecret: string
): Promise<PinterestAdsTokenResponse> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    scope: PINTEREST_ADS_SCOPES.join(" "),
  });

  const result = await safeFetch(
    PINTEREST_TOKEN_URL,
    {
      method: "POST",
      headers: {
        Authorization: buildBasicAuthHeader(clientId, clientSecret),
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
    },
    TOKEN_EXCHANGE_TIMEOUT_MS
  );

  if (!result.success) {
    const detail = result.error.providerMessage
      ? extractOAuthErrorDescription(result.error.providerMessage)
      : "";
    const oauthError = extractOAuthErrorCode(result.error.providerMessage);
    throw new PinterestAdsTokenError(
      sanitizeErrorMessage(
        `Pinterest Ads token refresh failed: ${detail || result.error.message}`
      ),
      { oauthError }
    );
  }

  const response = result.data;
  const data = (await response.json()) as PinterestAdsTokenResponse;

  // Refresh responses MAY omit refresh_token (when Pinterest chooses not to
  // rotate, though continuous-refresh tokens almost always do). The caller
  // (pinterestAdsAdapter.refreshToken) handles the missing value by reusing
  // the current refresh_token.
  validateTokenResponse(data, /* requireRefreshToken */ false);

  return data;
}

// ============================================================================
// Internal: token-response validation (mirrors MS Ads review #3 H2)
// ============================================================================

/**
 * Validates that a Pinterest token response has the fields we require and
 * that they're the correct type. Pinterest documents `expires_in` as an
 * integer but a future schema drift (e.g., `"2592000"` string) would silently
 * break `Date.now() + expires_in * 1000` — string-multiplication yields NaN.
 * We catch that here and throw a clear error instead.
 */
function validateTokenResponse(
  data: PinterestAdsTokenResponse,
  requireRefreshToken: boolean
): void {
  if (typeof data.access_token !== "string" || data.access_token.length === 0) {
    throw new Error("Invalid Pinterest Ads token response: missing access_token");
  }
  if (requireRefreshToken) {
    if (
      typeof data.refresh_token !== "string" ||
      data.refresh_token.length === 0
    ) {
      throw new Error(
        "Invalid Pinterest Ads token response: missing refresh_token"
      );
    }
  }
  if (typeof data.expires_in !== "number" || !Number.isFinite(data.expires_in)) {
    throw new Error(
      `Invalid Pinterest Ads token response: expires_in must be a finite number (received ${typeof data.expires_in})`
    );
  }
  // refresh_token_expires_in is optional but if present must be a finite number
  // (same NaN-prevention concern as expires_in).
  if (
    data.refresh_token_expires_in !== undefined &&
    (typeof data.refresh_token_expires_in !== "number" ||
      !Number.isFinite(data.refresh_token_expires_in))
  ) {
    throw new Error(
      `Invalid Pinterest Ads token response: refresh_token_expires_in must be a finite number when present (received ${typeof data.refresh_token_expires_in})`
    );
  }
}

// ============================================================================
// Callback Error Parsing
// ============================================================================

/**
 * Parses Pinterest API v5 OAuth callback errors from query params.
 *
 * Pinterest returns standard OAuth 2.0 error codes as `?error=...&error_description=...`.
 * We map them to user-facing messages and a retry hint.
 */
export function parsePinterestAdsCallbackError(
  searchParams: URLSearchParams
): PinterestAdsCallbackError | null {
  const error = searchParams.get("error");
  if (!error) return null;

  if (error === "access_denied") {
    return {
      code: "oauth-denied",
      userMessage: "You denied the Pinterest Advertising connection request.",
      userAction: "retry",
    };
  }

  if (
    error === "invalid_request" ||
    error === "unauthorized_client" ||
    error === "redirect_uri_mismatch"
  ) {
    return {
      code: "oauth-config-error",
      userMessage:
        "Pinterest Advertising connection is misconfigured. Contact support.",
      userAction: "none",
    };
  }

  if (error === "server_error" || error === "temporarily_unavailable") {
    return {
      code: "oauth-error",
      userMessage:
        "Pinterest Advertising is temporarily unavailable. Please try again later.",
      userAction: "retry",
    };
  }

  if (error === "invalid_scope") {
    return {
      code: "oauth-config-error",
      userMessage:
        "Pinterest Advertising connection is misconfigured (invalid scopes). Contact support.",
      userAction: "none",
    };
  }

  return {
    code: "oauth-error",
    userMessage:
      "An error occurred connecting to Pinterest Advertising. Please try again.",
    userAction: "retry",
  };
}

// ============================================================================
// Internal: extract the OAuth `error`/`error_description` from a sanitized body
// ============================================================================
//
// Pinterest token errors come back as a JSON envelope:
//   { "code": <int>, "message": "...", "status": "failure" }
// or the standard OAuth 2.0 form:
//   { "error": "invalid_grant", "error_description": "..." }
// `safeFetch` exposes the (sanitized) response body via `providerMessage`. We
// pluck out the most descriptive field so adapter callers can detect
// `invalid_grant` and prompt re-consent without having to re-parse the body.

function extractOAuthErrorDescription(providerBody: string): string {
  try {
    const parsed = JSON.parse(providerBody) as {
      error?: unknown;
      error_description?: unknown;
      message?: unknown;
      code?: unknown;
    };
    const err =
      typeof parsed.error === "string" && parsed.error.length > 0
        ? parsed.error
        : "";
    const desc =
      typeof parsed.error_description === "string" &&
      parsed.error_description.length > 0
        ? parsed.error_description
        : "";
    const msg =
      typeof parsed.message === "string" && parsed.message.length > 0
        ? parsed.message
        : "";

    if (err && desc) return `${err}: ${desc}`;
    if (desc) return desc;
    if (err) return err;
    if (msg) return msg;
    return "";
  } catch {
    // Not JSON — return empty string so the caller falls back to safeFetch's
    // synthetic message ("HTTP 400 from api.pinterest.com: ..."). Pinterest
    // consistently returns JSON on token-endpoint failures, so this branch is
    // a defensive fallback only.
    return "";
  }
}
